import { Box, Center, Flex, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import React, { useMemo } from "react";
import { ProjectAnalysisPopover } from "../ProjectAnalysisPopover";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";

interface BarometerChartProps {
  topMarker?: {
    label: string;
    value: number;
  };
  bottomMarker?: {
    label: string;
    value: number;
  };
  projectId?: string;
  blurContent?: boolean; // Optional prop to blur content
}

export const BarometerChart: React.FC<BarometerChartProps> = ({ topMarker, bottomMarker, projectId, blurContent }) => {
  const clampValue = (value: number): number => Math.min(Math.max(value, 0), 100);
  const colorMode = useColorModeValue("300", "900");
  const indicatorLabelColor = useColorModeValue("gray.600", "gray.400");
  const indicatorColor = useColorModeValue("primary.default", "gray.400");

  const positions = useMemo(
    () => ({
      top: clampValue((topMarker && topMarker.value) || 0),
      bottom: clampValue((bottomMarker && bottomMarker.value) || 0),
    }),
    [topMarker, bottomMarker]
  );

  const Indicator = ({ position, label, isTop }: { position: number; label: string; isTop: boolean }) => (
    <Box
      position="absolute"
      left={`${position}%`}
      transform="translateX(-50%)"
      {...(isTop ? { top: "-25px" } : { bottom: "-30px" })}
      width="100%"
      className={`scale-bar-indicator-${label.toLowerCase()}`}>
      {isTop ? (
        <>
          <Text fontWeight="semibold" color={indicatorLabelColor} fontSize="12px" textAlign="center" className="scale-bar-indicator-label">
            {label}
          </Text>
          <Center>
            <Box
              width="0"
              height="0"
              borderLeft="12px solid transparent"
              borderRight="12px solid transparent"
              borderTop="16px solid"
              borderTopColor={indicatorColor}
              className="scale-bar-indicator-arrow"
            />
          </Center>
        </>
      ) : (
        <Stack direction="column" spacing="0" height="100%">
          <Center>
            <TriangleUpIcon color={indicatorColor} boxSize="1.4rem" />
          </Center>
          <Text fontWeight="semibold" color={indicatorLabelColor} fontSize="12px" textAlign="center" className="scale-bar-indicator-label">
            {label}
          </Text>
        </Stack>
      )}
    </Box>
  );

  return (
    <Box position="relative" width="100%" className="scale-bar-container">
      <Flex justifyContent="space-between" mb={1} filter={blurContent ? "blur(4px)" : "none"}>
        <Stack direction="row" spacing="3px" height="100%">
          <Text color={indicatorColor} fontSize="xs" fontWeight="semibold">
            Sell
          </Text>
          <Center>
            <TriangleDownIcon color={indicatorColor} transform="rotate(-90deg)" boxSize="0.8rem" />
          </Center>
        </Stack>
        <Text color={indicatorColor} fontSize="xs" fontWeight="semibold">
          Hold
        </Text>
        <Stack direction="row" spacing="3px" height="100%">
          <Center>
            <TriangleDownIcon color={indicatorColor} transform="rotate(90deg)" boxSize="0.8rem" />
          </Center>
          <Text color={indicatorColor} fontSize="xs" fontWeight="semibold">
            Buy
          </Text>
        </Stack>
      </Flex>
      <ProjectAnalysisPopover projectId={projectId}>
        <Box
          height="2rem"
          bgGradient={`linear(to-r, red.${colorMode}, yellow.${colorMode}, green.${colorMode})`}
          position="relative"
          borderRadius="md"
          cursor="default"
          className="scale-bar-gradient">
          {topMarker && topMarker.value > 0 && (
            <Box position="absolute" width="100%" height="100%" top="0" bottom="-25px" filter={blurContent ? "blur(4px)" : "none"}>
              <Indicator position={positions.top} label={topMarker.label} isTop />
            </Box>
          )}
          {bottomMarker && bottomMarker.value > 0 && (
            <Box position="absolute" width="100%" height="100%" top="0" bottom="-25px" filter={blurContent ? "blur(4px)" : "none"}>
              <Indicator position={positions.bottom} label={bottomMarker.label} isTop={false} />
            </Box>
          )}
        </Box>
      </ProjectAnalysisPopover>
    </Box>
  );
};
